import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { authMode, bootOidc } from './auth/oidc';
import { AuthScreen } from './ui/AuthScreen';
import './styles.css';

const root = createRoot(document.getElementById('root')!);
const render = (node: ReactNode) => root.render(<StrictMode>{node}</StrictMode>);

/**
 * OIDC mode signs in before the app modules load. config.ts reads the query string at import time, so it must see the
 * URL after the callback parameters are replaced with the original return path (?shop=, ?perf= survive the round trip).
 * Static mode and ?stress= skip all of this and render exactly as before.
 */
async function boot() {
  const stress = !!new URLSearchParams(window.location.search).get('stress');
  if (authMode === 'oidc' && !stress) {
    render(<AuthScreen kind="loading" />);
    const r = await bootOidc().catch((e: Error) => ({ kind: 'error' as const, message: e.message }));
    if (r.kind === 'iframe') return;
    if (r.kind !== 'signed-in') return render(<AuthScreen kind={r.kind} message={r.kind === 'error' ? r.message : undefined} />);
  }

  const [{ default: App }, { liveStore }, { useUi }, { api }] = await Promise.all([
    import('./App'), import('./live/liveStore'), import('./store'), import('./api'),
  ]);

  // Development only: lets browser tests and the console drive the UI (e.g. __idrp.ui.getState().select('P10')).
  if (import.meta.env.DEV) (window as unknown as { __idrp: object }).__idrp = { ui: useUi, live: liveStore };

  if (!stress) {
    // Warn when this build and the server disagree on auth (every call would 401). Does not block the app.
    api.health().then((h) => {
      // Demo drill panel only when the server has the endpoints switched on. Absent (older backend) = off.
      useUi.getState().setDemoControls(h.demoControls === true);
      if (h.authMode && h.authMode !== authMode) {
        useUi.getState().setAuthNotice(`The server uses ${h.authMode} auth but this build uses ${authMode}. Requests will be refused.`);
      }
    }).catch(() => undefined);
  }

  if (authMode === 'oidc' && !stress) {
    // The role comes only from the server. A 401 here has already been through one silent renew.
    const me = await api.me().catch((e: Error) => { render(<AuthScreen kind="error" message={e.message} />); return undefined; });
    if (me === undefined) return;
    if (!me) return render(<AuthScreen kind="error" message="GET /api/me returned no user" />);
    useUi.getState().setSession(me.user, me.role);
  }

  render(<App />);
}

void boot();
